import { Box, Button, Flex, FormLabel, Grid, Input, InputGroup, InputRightAddon, Stack, Text } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import useWorkshopQuery from 'hooks/useWorkshopQuery';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosCopy } from 'react-icons/io';
import { MdOpenInNew } from 'react-icons/md';
import { useCopyToClipboard } from 'usehooks-ts';

import AddonIconButton from '../../components/form-elements/AddonIconButton';
import { CheckIcon } from '../../components/form-elements/RadioCardGroup';
import AlertModal from '../../components/modals/AlertModal';
import { DataTable } from '../../components/Table/DataTable';
import localconfig from '../../config';
import useCreateSupportUserMutation from '../../hooks/mutations/useCreateSupportUserMutation';
import useDeleteSupportUserMutation from '../../hooks/mutations/useDeleteSupportUserMutation';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopUserOverviewCard = (props: { workshopId: number }) => {
  const { t } = useTranslation();
  // @ts-ignore
  const { workshopId } = props;
  const deleteSupportUserMutation = useDeleteSupportUserMutation(['workshop', workshopId]);
  const createSupportUserMutation = useCreateSupportUserMutation(['workshop', workshopId]);
  const workshopQuery = useWorkshopQuery(workshopId);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [value, copy] = useCopyToClipboard();

  const columns = useMemo(
    () => [
      {
        Header: t('forms:id.label'),
        accessor: 'id',
      },
      {
        Header: t('workshop:email.label'),
        accessor: 'email',
      },
      {
        Header: t('workshop:firstname.label'),
        accessor: 'firstname',
      },
      {
        Header: t('workshop:lastname.label'),
        accessor: 'lastname',
      },
      {
        Header: t('workshop:users.types.label'),
        accessor: (row: { type: string }) => t('workshop:users.types.' + row?.type),
      },
      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          if (row.original.type === 'support') {
            return (
              <Flex alignContent={'flex-end'}>
                <Button
                  ml={'auto'}
                  onClick={() => {
                    NiceModal.show(AlertModal as any, {
                      onSubmit: () => deleteSupportUserMutation.mutate(row.original.id),
                      children: t('workshop:users.modals.delete.description'),
                      content: {
                        header: t('workshop:users.modals.delete.header'),
                        footer: {
                          buttons: {
                            cancelCaption: t('common:cancel'),
                            actionCaption: t('common:delete'),
                          },
                        },
                      },
                    });
                  }}
                >
                  {t('common:delete')}
                </Button>
              </Flex>
            );
          }
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workshopQuery.data?.users],
  );

  return (
    <ContentCard
      isLoading={workshopQuery.isLoading || workshopQuery.isFetching}
      minH="fit-content"
      header={t('workshop:users.header')}
      data-test-id="users-overview-card"
    >
      <Button
        variant="primary"
        width="min-content"
        onClick={() => {
          createSupportUserMutation.mutateAsync({ workshopId }).then((data: object) => {
            NiceModal.show(AlertModal as any, {
              onSubmit: () => {
                window.location.reload();
              },
              children: (
                <Grid>
                  <Text>{t('workshop:users.modals.display.description')}</Text>

                  <Button
                    rightIcon={<MdOpenInNew />}
                    variant="primary"
                    size="sm"
                    as={'a'}
                    target="_blank"
                    data-test-id={`open-credential-button`}
                    href={localconfig.wpBaseUrl}
                    flex={0}
                    justifySelf={'end'}
                  >
                    {t('workshop:users.modals.display.open')}
                  </Button>

                  <Grid gap={4} py={4}>
                    <Box>
                      <FormLabel size="lg">
                        <Text fontSize="xs">{t('workshop:email.label')}</Text>
                      </FormLabel>
                      <InputGroup>
                        {/* @ts-ignore*/}
                        <Input type={'text'} value={data?.email} data-lpignore={true} />

                        <InputRightAddon p={'0'}>
                          <AddonIconButton
                            //@ts-ignore
                            icon={value === data?.email ? <CheckIcon /> : <IoIosCopy />}
                            //@ts-ignore
                            onClick={() => copy(data?.email)}
                            aria-label={'copy button'}
                          />
                        </InputRightAddon>
                      </InputGroup>
                    </Box>
                    <Box>
                      <FormLabel size="lg">
                        <Text fontSize="xs">{t('workshop:password.label')}</Text>
                      </FormLabel>
                      <InputGroup>
                        {/* @ts-ignore*/}
                        <Input type={'text'} value={data?.password} data-lpignore={true} />

                        <InputRightAddon p={'0'}>
                          <AddonIconButton
                            icon={
                              //@ts-ignore
                              value === data?.password ? <CheckIcon /> : <IoIosCopy />
                            }
                            //@ts-ignore
                            onClick={() => copy(data?.password)}
                            aria-label={'copy button'}
                          />
                        </InputRightAddon>
                      </InputGroup>
                    </Box>
                  </Grid>
                </Grid>
              ),
              content: {
                header: t('workshop:users.modals.display.header'),
                footer: {
                  buttons: {
                    actionCaption: t('common:ok'),
                  },
                },
              },
            });
          });
        }}
      >
        {t('workshop:users.types.create')}
      </Button>
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w="full">
          <DataTable
            data-test-id="users-table"
            data={workshopQuery.data?.users}
            hasFilters={false}
            columns={columns}
            loading={workshopQuery.isLoading || workshopQuery.isFetching}
            isMoreThenOnePage={false}
            hasSearchInput={undefined as any}
          />
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default WorkshopUserOverviewCard;
